import { bytesToNDigits } from '@mail/cross-sizes-utils';
import { Icon16DownloadOutline, Icon16ViewOutline, Icon20Chain, Icon20UsersOutline } from '@vkontakte/icons';
import { IconButton, Spacing, Text } from '@vkontakte/vkui';
import classNames from 'clsx';
import React, { memo, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { publishHelper } from 'reactApp/appHelpers/publishHelper';
import { toolbarActions } from 'reactApp/appHelpers/toolbarActions';
import { ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import { applyTheme } from 'reactApp/hooks/useDarkTheme';
import { getItemById } from 'reactApp/modules/storage/storage.selectors';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { ViewerSelectors } from 'reactApp/modules/viewer/viewer.selectors';
import type { RootState } from 'reactApp/store';
import { Dialog } from 'reactApp/ui/Dialog/Dialog';
import { Hint } from 'reactApp/ui/Hint/Hint';
import { NameComponent } from 'reactApp/ui/Name/Name';
import { formatLocale } from 'reactApp/utils/formatDate';
import { sendDwh } from 'reactApp/utils/ga';
import { ECategoryGa } from 'reactApp/utils/paymentGa';
import { isDarkThemeModeEnabled } from 'reactApp/utils/theme';

import { useCounter } from './hooks/useCounter';
import { useShowShare } from './hooks/useShowShare';
import styles from './PublicInfoModal.css';
import { getPublicItemType } from './PublicInfoModal.helpers';
import type { PublicInfoModalPorps } from './PublicInfoModal.types';

export const PublicInfoModal = memo<PublicInfoModalPorps>(({ id, qaId, onClose, place }) => {
    const cloudItem = useSelector((state: RootState) => getItemById(state, id));
    const { parent, name, kind } = cloudItem || {};
    const size = cloudItem && 'size' in cloudItem && cloudItem?.size;
    const creationDate = cloudItem && 'ctime' in cloudItem && cloudItem?.ctime;

    const { isLoading: userInfoLoading } = useSelector(UserSelectors.getLifeCycleState);

    const counter = useCounter(place, cloudItem?.id || id, kind === 'album');
    const showShare = useShowShare();

    const type = getPublicItemType(cloudItem);
    const placement = parent === ROOT_FOLDER_ID ? 'Все файлы' : parent?.split('/').pop();

    const isViewerActive = useSelector(ViewerSelectors.isViewerActive);
    const isCurrentDarkTheme = isDarkThemeModeEnabled();

    useEffect(() => {
        if (isViewerActive) {
            applyTheme(true);
        }

        return () => {
            applyTheme(isCurrentDarkTheme);
        };
    }, []);

    useEffect(() => {
        emitAnalyticEvent(AnalyticEventNames.PUBLIC_INFO_MODAL_SHOW);
        sendDwh({
            eventCategory: ECategoryGa.features,
            action: 'show',
            dwhData: {
                place,
                platform: 'web',
                webview: false,
            },
        });
    }, []);

    const handlePublish = useCallback(() => {
        onClose?.();
        publishHelper({ item: cloudItem, publishFrom: 'info-modal' });
    }, [cloudItem, onClose]);

    const handleShare = useCallback(() => {
        onClose?.();
        toolbarActions.share(id);
    }, [id, onClose]);

    const handleClose = useCallback(() => {
        onClose?.();
        sendDwh({
            eventCategory: ECategoryGa.features,
            action: 'close',
            dwhData: {
                place,
                platform: 'web',
                webview: false,
            },
        });
    }, [onClose]);

    return (
        <Dialog
            open
            closable
            id={qaId || 'public-info-modal'}
            header={
                <>
                    <NameComponent name={name} truncate showTooltip />
                </>
            }
            size="small"
            className={styles.root}
            onCancel={handleClose}
        >
            <Spacing size={4} />
            <div className={styles.group}>
                <div className={styles.row} data-qa-id="share">
                    <div>
                        <Text weight="1">Общий доступ</Text>
                        <Text className={styles.caption}>Нажмите, чтобы настроить</Text>
                    </div>
                    <div className={styles.shareButtons}>
                        <IconButton label="Поделиться" className={styles.shareIcon} onClick={handlePublish} data-qa-id="icon-publish">
                            <Hint text="Поделиться">
                                <Icon20Chain />
                            </Hint>
                        </IconButton>
                        {cloudItem?.isFolder && showShare && (
                            <IconButton
                                label="Настроить доступ"
                                className={styles.accessIcon}
                                onClick={handleShare}
                                data-qa-id="icon-share"
                            >
                                <Hint text="Настроить доступ">
                                    <Icon20UsersOutline />
                                </Hint>
                            </IconButton>
                        )}
                    </div>
                </div>
            </div>
            <Spacing size={8} />
            <div className={styles.group}>
                <div className={styles.row} data-qa-id="type">
                    <Text data-qa-id="label">Тип:</Text>
                    <Text data-qa-id="value">{type}</Text>
                </div>
                {Boolean(size) && (
                    <>
                        <Spacing size={12} />
                        <div className={styles.row} data-qa-id="size">
                            <Text data-qa-id="label">Размер:</Text>
                            <Text data-qa-id="value">{bytesToNDigits(size || 0, 3).value}</Text>
                        </div>
                    </>
                )}

                {creationDate && (
                    <>
                        <Spacing size={12} />
                        <div className={styles.row} data-qa-id="created">
                            <Text data-qa-id="label">Создан:</Text>
                            <Text data-qa-id="value">{formatLocale(creationDate * 1000, 'dd.MM.yyyy • HH:mm')}</Text>
                        </div>
                    </>
                )}

                {parent && kind !== 'album' && placement && (
                    <>
                        <Spacing size={12} />
                        <div className={styles.row} data-qa-id="placement">
                            <Text data-qa-id="label">Расположение:</Text>
                            <Hint text={placement}>
                                <Text data-qa-id="value" className={styles.placement}>
                                    {placement}
                                </Text>
                            </Hint>
                        </div>
                    </>
                )}
            </div>
            <Spacing size={8} />
            {userInfoLoading ? (
                <div className={classNames(styles.group, styles.skeleton)} />
            ) : (
                <div className={styles.group}>
                    <div className={styles.row} data-qa-id="views-count">
                        <Text data-qa-id="label">Просмотров:</Text>
                        <div className={styles.counter}>
                            <Icon16ViewOutline />
                            <Text data-qa-id="value">{counter?.views || 0}</Text>
                        </div>
                    </div>
                    <Spacing size={12} />
                    <div className={styles.row} data-qa-id="download-count">
                        <Text data-qa-id="label">Скачиваний:</Text>
                        <div className={styles.counter}>
                            <Icon16DownloadOutline data-qa-id="icon-downloads" />
                            <Text data-qa-id="value">{counter?.downloads || 0}</Text>
                        </div>
                    </div>
                </div>
            )}
        </Dialog>
    );
});

PublicInfoModal.displayName = 'PublicInfoModal';
